import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { nuclearn_api_url } from "src/App";
import type {
  Group,
  GroupFolderGrantsResponse,
  GroupListResponse,
  GroupMembersResponse,
} from "src/api/projectFolders/types";
import { AA_EVENTS, captureEvent } from "src/config/posthog";

// The server clamps limit to 500. The share-folder picker fetches at the cap
// in one shot (it needs every group as Autocomplete options); the admin index
// uses useGroupsList for server-side pagination + search instead.
export const GROUPS_FETCH_LIMIT = 500;

// === LIST ALL GROUPS (share picker bulk fetch) ===
const getGroups = (): Promise<GroupListResponse> => {
  return axios
    .get(`${nuclearn_api_url}/groups/`, {
      params: { limit: GROUPS_FETCH_LIMIT },
    })
    .then((res) => res.data);
};

export const useGroups = () => {
  return useQuery(["groups"], getGroups, {
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
};

// === PAGINATED + SEARCHABLE GROUPS LIST (admin index) ===
// Distinct from useGroups (the share picker's bulk fetch): this drives the
// server-paginated groups index. `invalidateQueries(["groups"])` (create /
// delete / membership changes) prefix-matches this key, keeping it fresh.
export interface GroupsListQuery {
  limit: number;
  offset: number;
  search?: string;
}

const getGroupsList = (q: GroupsListQuery): Promise<GroupListResponse> =>
  axios
    .get(`${nuclearn_api_url}/groups/`, {
      params: {
        limit: q.limit,
        offset: q.offset,
        ...(q.search ? { search: q.search } : {}),
      },
    })
    .then((res) => res.data);

export const useGroupsList = (q: GroupsListQuery) => {
  return useQuery(["groups", "list", q], () => getGroupsList(q), {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    staleTime: 10_000,
  });
};

// === MY GROUPS (any user) ===
const getMyGroups = (): Promise<Group[]> => {
  return axios
    .get(`${nuclearn_api_url}/groups/my-groups`)
    .then((res) => res.data);
};

export const useMyGroups = () => {
  return useQuery(["groups", "my-groups"], getMyGroups, {
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
};

// === FOLDER GRANTS HELD BY A GROUP (admin) ===
const getGroupFolderGrants = (id: number): Promise<GroupFolderGrantsResponse> =>
  axios
    .get(`${nuclearn_api_url}/groups/${id}/folder-grants`)
    .then((res) => res.data);

export const useGroupFolderGrants = (id: number | null, enabled = true) => {
  return useQuery(
    ["groups", id, "folder-grants"],
    () => getGroupFolderGrants(id!),
    {
      enabled: enabled && id !== null,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  );
};

// === DATASET GRANTS HELD BY A GROUP (admin) ===
export interface GroupDatasetGrant {
  dataset_id: number;
  dataset_name: string;
  permission_level: "read" | "write" | "manage";
  dataset_deleted: boolean;
}
// Reached through a folder grant, so there is no dataset grant row to remove —
// revoking has to target folder_id. Kept out of `items` so a revoke request
// built from that list can never silently no-op.
export interface GroupInheritedDatasetGrant extends GroupDatasetGrant {
  folder_id: number;
  folder_name: string;
}
// A grant on a dataset FOLDER. Revocable — unlike the inherited rows this is a
// real grant row, and it is the one to remove to end the reach they describe.
// Not the same as folder-grants, which covers project folders.
export interface GroupDatasetFolderGrant {
  folder_id: number;
  folder_name: string;
  permission_level: "read" | "write" | "manage";
}
export interface GroupDatasetGrantsResponse {
  items: GroupDatasetGrant[];
  // Both absent on backends predating folder-derived reporting.
  dataset_folder_items?: GroupDatasetFolderGrant[];
  inherited_items?: GroupInheritedDatasetGrant[];
}

const getGroupDatasetGrants = (
  id: number,
): Promise<GroupDatasetGrantsResponse> =>
  axios
    .get(`${nuclearn_api_url}/groups/${id}/dataset-grants`)
    .then((res) => res.data);

export const useGroupDatasetGrants = (id: number | null, enabled = true) => {
  return useQuery(
    ["groups", id, "dataset-grants"],
    () => getGroupDatasetGrants(id!),
    {
      enabled: enabled && id !== null,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  );
};

// === REVOKE A GROUP'S RESOURCE GRANTS (single or bulk, admin) ===
export interface RevokeGroupGrantItem {
  resource_type: "dataset" | "folder" | "dataset_folder";
  resource_id: number;
}

const revokeGroupGrants = ({
  groupId,
  items,
}: {
  groupId: number;
  items: RevokeGroupGrantItem[];
}): Promise<{ revoked: number; requested: number }> =>
  axios
    .post(`${nuclearn_api_url}/groups/${groupId}/revoke-grants`, { items })
    .then((res) => res.data);

export const useRevokeGroupGrants = () => {
  const queryClient = useQueryClient();
  return useMutation(revokeGroupGrants, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries([
        "groups",
        variables.groupId,
        "folder-grants",
      ]);
      queryClient.invalidateQueries([
        "groups",
        variables.groupId,
        "dataset-grants",
      ]);
      // Revoking a grant changes what the share dialogs show (folder grants
      // surface as inherited rows on every contained dataset), so drop them
      // all — id-less prefixes match every open dialog. "folder" items are
      // project folders, same key useDeleteGroup already drops.
      queryClient.invalidateQueries(["dataset-shares"]);
      queryClient.invalidateQueries(["dataset-folder-shares"]);
      queryClient.invalidateQueries(["project-folder-shares"]);
    },
  });
};

// === GET SINGLE GROUP WITH MEMBERS ===
// Group metadata + member_count. Members come from useGroupMembers (paged).
const getGroup = (id: number): Promise<Group> => {
  return axios.get(`${nuclearn_api_url}/groups/${id}`).then((res) => res.data);
};

export const useGroup = (id: number | null, enabled = true) => {
  return useQuery(["groups", id], () => getGroup(id!), {
    enabled: enabled && id !== null,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
};

// === PAGINATED + SEARCHABLE MEMBERS (admin) ===
export interface GroupMembersQuery {
  limit: number;
  offset: number;
  search?: string;
}

const getGroupMembers = (
  id: number,
  query: GroupMembersQuery,
): Promise<GroupMembersResponse> =>
  axios
    .get(`${nuclearn_api_url}/groups/${id}/members`, {
      params: {
        limit: query.limit,
        offset: query.offset,
        ...(query.search ? { search: query.search } : {}),
      },
    })
    .then((res) => res.data);

export const useGroupMembers = (
  id: number | null,
  query: GroupMembersQuery,
  enabled = true,
) => {
  return useQuery(
    ["groups", id, "members", query],
    () => getGroupMembers(id!, query),
    {
      enabled: enabled && id !== null,
      // Smooth server-side pagination: hold the prior page while the next
      // one loads instead of flashing empty.
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
  );
};

// === CREATE GROUP ===
const createGroup = (data: {
  name: string;
  description?: string;
  member_user_ids?: number[];
}): Promise<Group> => {
  return axios
    .post(`${nuclearn_api_url}/groups/`, data)
    .then((res) => res.data);
};

export const useCreateGroup = () => {
  const queryClient = useQueryClient();
  return useMutation(createGroup, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["groups"]);
      captureEvent(AA_EVENTS.GROUP_CREATED, {
        group_id: data?.id,
        name_length: data?.name?.length ?? 0,
      });
    },
  });
};

// === UPDATE GROUP ===
const updateGroup = ({
  id,
  data,
}: {
  id: number;
  data: { name?: string; description?: string };
}): Promise<Group> => {
  return axios
    .patch(`${nuclearn_api_url}/groups/${id}`, data)
    .then((res) => res.data);
};

export const useUpdateGroup = () => {
  const queryClient = useQueryClient();
  return useMutation(updateGroup, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(["groups"]);
      queryClient.invalidateQueries(["groups", variables.id]);
    },
  });
};

// === DELETE GROUP ===
const deleteGroup = (id: number): Promise<void> => {
  return axios.delete(`${nuclearn_api_url}/groups/${id}`);
};

export const useDeleteGroup = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteGroup, {
    onSuccess: (_data, groupId) => {
      queryClient.invalidateQueries(["groups"]);
      // Deleting a group cascade-revokes its folder shares server-side. We
      // can't enumerate which folders were touched here without a server
      // round-trip, so refresh the visible-roots list (membership scope can
      // shrink) and the share lists; the SSE folder-permission-changed
      // events that the BE emits cover the deeper per-folder caches.
      queryClient.invalidateQueries(["project-folder-shares"]);
      queryClient.invalidateQueries(["project-folders", "root"]);
      captureEvent(AA_EVENTS.GROUP_DELETED, { group_id: groupId });
    },
  });
};

// === ADD MEMBER TO GROUP ===
const addMember = ({
  groupId,
  userId,
}: {
  groupId: number;
  userId: number;
}): Promise<void> => {
  return axios.post(`${nuclearn_api_url}/groups/${groupId}/members`, {
    user_id: userId,
  });
};

export const useAddMember = () => {
  const queryClient = useQueryClient();
  return useMutation(addMember, {
    onSuccess: (_data, variables) => {
      // Prefix-invalidate "groups": refreshes BOTH the detail (["groups", id])
      // and the list (["groups"], which feeds each card's member_count). The
      // narrower ["groups", id] key does not match the bare list key, so the
      // count would otherwise stay stale until a manual refresh.
      queryClient.invalidateQueries(["groups"]);
      captureEvent(AA_EVENTS.GROUP_MEMBER_ADDED, {
        group_id: variables.groupId,
        user_id: variables.userId,
      });
    },
  });
};

// === REMOVE MEMBER FROM GROUP ===
const removeMember = ({
  groupId,
  userId,
}: {
  groupId: number;
  userId: number;
}): Promise<void> => {
  return axios.delete(
    `${nuclearn_api_url}/groups/${groupId}/members/${userId}`,
  );
};

export const useRemoveMember = () => {
  const queryClient = useQueryClient();
  return useMutation(removeMember, {
    onSuccess: (_data, variables) => {
      // Prefix-invalidate so the list's member_count refreshes too (see add).
      queryClient.invalidateQueries(["groups"]);
      captureEvent(AA_EVENTS.GROUP_MEMBER_REMOVED, {
        group_id: variables.groupId,
        user_id: variables.userId,
      });
    },
  });
};
